import {
  BgColor,
  bgColorClassName,
  borderClassName,
  BorderColor,
  FgColor,
  fgColorClassName,
  hoverBgColorClassName,
} from "./colors";
import {
  alignItemsMapping,
  Breakpoint,
  containerClassName,
  fontWeightMapping,
  justifyContentMapping,
  justifyItemsMapping,
  justifySelfMapping,
  MaxWidth,
  maxWidthClassName,
  MinHeight,
  minHeightClassName,
  spacingClassName,
  SpacingPrefix,
  SpacingSize,
  textSizeMapping,
} from "./tailwindMappings";
import { HTMLProps, ReactNode } from "react";
import { classNames } from "@/utils/classNames";
import { TMessage } from "@/i18n/tt";

export type CommonProps = {
  /** Test ID for selecting elements in tests. */
  testId?: string;
  /** Test type marker for selecting elements by component type in tests. */
  testType?: string;
  /** Additional CSS class name(s). Prefer using Box props over custom classes. */
  className?: string;
  /** Position the element absolutely to fill the entire viewport. */
  fullscreen?: boolean;
  /** Set height to 100% of the parent. */
  fullheight?: boolean;
  /** Background color. `true` defaults to the theme default background. */
  bg?: BgColor | boolean;
  /** Foreground (text) color. `true` defaults to the theme default foreground. */
  fg?: FgColor | boolean;
  /** Border on all sides. `true` uses the default gray border color. */
  border?: BorderColor | boolean;
  /** Border on the bottom edge only. */
  borderBottom?: BorderColor | boolean;
  /** Border on the left edge only. */
  borderLeft?: BorderColor | boolean;
  /** Border on the right edge only. */
  borderRight?: BorderColor | boolean;
  /** Border on the top edge only. */
  borderTop?: BorderColor | boolean;
  /** Padding on all sides. `true` defaults to `"md"`. */
  padding?: "xs" | "sm" | "md" | "lg" | boolean;
  /** Horizontal padding (left + right). `true` defaults to `"md"`. */
  paddingX?: "xs" | "sm" | "md" | "lg" | boolean;
  /** Vertical padding (top + bottom). `true` defaults to `"md"`. */
  paddingY?: "xs" | "sm" | "md" | "lg" | boolean;
  /** Padding on the top edge only. `true` defaults to `"md"`. */
  paddingTop?: "xs" | "sm" | "md" | "lg" | "xl" | boolean;
  /** Padding on the bottom edge only. `true` defaults to `"md"`. */
  paddingBottom?: "xs" | "sm" | "md" | "lg" | boolean;
  /** Padding on the left edge only. `true` defaults to `"md"`. */
  paddingLeft?: "xs" | "sm" | "md" | "lg" | boolean;
  /** Padding on the right edge only. `true` defaults to `"md"`. */
  paddingRight?: "xs" | "sm" | "md" | "lg" | boolean;
  /** Horizontal margin (left + right). `"auto"` centers the element. */
  marginX?: "auto" | "sm" | "md" | "lg" | boolean;
  /** Vertical margin (top + bottom). */
  marginY?: "auto" | "sm" | "md" | "lg" | boolean;
  /** Left margin. `"auto"` pushes the element to the right. */
  marginLeft?: "auto" | "sm" | "md" | "lg" | boolean;
  /** Right margin. `"auto"` pushes the element to the left. */
  marginRight?: "auto" | "sm" | "md" | "lg" | boolean;
  /** Top margin. */
  marginTop?: "auto" | "sm" | "md" | "lg" | boolean;
  /** Bottom margin. */
  marginBottom?: "auto" | "sm" | "md" | "lg" | boolean;
  /** Gap between children. The preferred way to space elements in a layout flow. `true` and `undefined` default to `"sm"`. Use `0` for no gap. */
  gap?: "xs" | "sm" | "md" | "lg" | "xl" | boolean | 0;
  /** Row gap (vertical spacing between wrapped rows). `true` defaults to `"sm"`. */
  rowGap?: "xs" | "sm" | "md" | "lg" | "xl" | boolean | 0;
  /** Column gap (horizontal spacing between columns). `true` defaults to `"sm"`. */
  columnGap?: "xs" | "sm" | "md" | "lg" | "xl" | boolean | 0;
  /** Overflow behavior on both axes. */
  overflow?: "auto" | "hidden";
  /** Vertical overflow behavior. */
  overflowY?: "auto" | "hidden";
  /** Horizontal overflow behavior. */
  overflowX?: "auto" | "hidden";
  /** Maximum width constraint. */
  maxWidth?: MaxWidth;
  minHeight?: MinHeight;
  /** Drop shadow. `true` defaults to a medium shadow. */
  dropShadow?: boolean | "lg" | "xl" | "top";
  /**
   * Distribution of space along the main axis. In a horizontal
   * box `"start"` lays out children from left to right (default),
   * `"end"` right-aligns, and `"center"` centers them.
   */
  justifyContent?: "start" | "end" | "center" | "space-between" | "space-around" | "space-evenly";
  /** Alignment of the element itself along the inline axis (for grid children). */
  justifySelf?: "start" | "end" | "center" | "stretch";
  /**
   * Cross-axis alignment of children. In a horizontal box, `"start"` means top,
   * `"end"` means bottom, and `"stretch"` fills from top to bottom.
   */
  alignItems?: "start" | "end" | "center" | "stretch" | "baseline";
  /** Inline-axis alignment of children in a block or grid layout. Ignored in flex. */
  justifyItems?: "start" | "end" | "center" | "stretch";
  /** Border radius. `true` uses the default radius, `"lg"` a larger one, `"full"` makes it circular. */
  rounded?: boolean | "lg" | "full";
  /** Border radius on the top edge only. */
  roundedTop?: boolean | "lg" | "full";
  /** Border radius on the bottom edge only. */
  roundedBottom?: boolean | "lg" | "full";
  /** Prevent text wrapping. */
  whitespace?: "nowrap";
  /** Text alignment. */
  textAlign?: "left" | "center" | "right";
  /** Text size preset. */
  textSize?: keyof typeof textSizeMapping;
  /** Font weight. Note: `"bold"` maps to `font-semibold` (500), not `font-bold` (600). */
  fontWeight?: keyof typeof fontWeightMapping;
  /** Italic text. */
  italic?: boolean;
  /** Center the element with max-width and horizontal padding. `true` uses the default breakpoint. */
  container?: boolean | Breakpoint;
  /** Allow the element to grow to fill available space. `"golden"` uses a 0.625 grow factor. */
  grow?: boolean | "golden";
  /** Tooltip text shown on hover. Supports TMessage for i18n. */
  tooltip?: string | TMessage;
  /** Highlight background on hover. */
  hoverBg?: boolean | BgColor;
};

export type BoxProps = CommonProps & {
  children?: ReactNode;
  /** Display mode. Defaults to `"flex"`. Use `"block"` for non-flex block layout. */
  display?: "flex" | "block" | "inline-flex";
  /** Stack children vertically instead of horizontally. VBox sets this to `true`. */
  vertical?: boolean;
  /** The HTML element for the box. Defaults to `div`. */
  element?: string;
  /** Flex shorthand. `"auto"` sizes based on content, `"none"` prevents growing/shrinking. */
  flex?: "auto" | "none";
  /** Allow children to wrap to the next line when they overflow. */
  wrap?: boolean;
  /** Make the element sticky within its scroll container. `true` and `"top"` stick to the top, `"bottom"` sticks to the bottom. */
  sticky?: boolean | "top" | "bottom";
  /** CSS class applied when the sticky element becomes pinned (stuck). */
  stuckClassName?: string;
  /** Callback fired when the sticky element becomes pinned or unpinned. */
  onPinnedChange?: (pinned: boolean) => void;
  /** Additional HTML attributes passed directly to the underlying `<div>`. */
  htmlProps?: Omit<HTMLProps<HTMLDivElement>, "className">;
};

const spacing = (
  prefix: SpacingPrefix,
  value: SpacingSize | "auto" | boolean | 0 | undefined,
  fallback: SpacingSize,
): string | undefined =>
  value ? spacingClassName(prefix, value === true ? fallback : value) : undefined;

export function commonPropsToClassName({
  className,
  fullscreen,
  fullheight,
  bg,
  fg,
  border,
  borderBottom,
  borderLeft,
  borderRight,
  borderTop,
  padding,
  paddingX,
  paddingY,
  paddingTop,
  paddingBottom,
  paddingLeft,
  paddingRight,
  marginX,
  marginY,
  marginLeft,
  marginRight,
  marginTop,
  marginBottom,
  gap,
  rowGap,
  columnGap,
  overflow,
  overflowY,
  overflowX,
  maxWidth,
  minHeight,
  dropShadow,
  justifyContent,
  justifyItems,
  justifySelf,
  alignItems,
  rounded,
  roundedTop,
  roundedBottom,
  whitespace,
  textAlign,
  textSize,
  fontWeight,
  italic,
  container,
  grow,
  hoverBg,
}: CommonProps): string {
  paddingX = paddingX ?? (container ? true : undefined);
  return classNames(
    className,
    fullscreen && "absolute inset-0 h-full w-full",
    fullheight && "h-full",
    bgColorClassName(bg),
    hoverBgColorClassName(hoverBg),
    fgColorClassName(fg),
    borderClassName("border", border),
    borderClassName("border-b", borderBottom),
    borderClassName("border-l", borderLeft),
    borderClassName("border-r", borderRight),
    borderClassName("border-t", borderTop),
    spacing("p", padding, "md"),
    spacing("px", paddingX, "md"),
    spacing("py", paddingY, "md"),
    spacing("pt", paddingTop, "md"),
    spacing("pb", paddingBottom, "md"),
    spacing("pl", paddingLeft, "md"),
    spacing("pr", paddingRight, "md"),
    spacing("mx", marginX, "md"),
    spacing("my", marginY, "md"),
    spacing("ml", marginLeft, "md"),
    spacing("mr", marginRight, "md"),
    spacing("mt", marginTop, "md"),
    spacing("mb", marginBottom, "md"),
    spacing("gap", gap ?? true, "sm"),
    spacing("gap-y", rowGap, "sm"),
    spacing("gap-x", columnGap, "sm"),
    overflow === "auto" && "overflow-auto",
    overflow === "hidden" && "overflow-hidden",
    overflowY === "auto" && "overflow-y-auto",
    overflowY === "hidden" && "overflow-y-hidden",
    overflowX === "auto" && "overflow-x-auto",
    overflowX === "hidden" && "overflow-x-hidden",
    maxWidth && maxWidthClassName(maxWidth),
    minHeight && minHeightClassName(minHeight),
    dropShadow === true && "shadow-md",
    dropShadow === "lg" && "shadow-lg",
    dropShadow === "top" && "shadow-top-lg",
    dropShadow === "xl" && "drop-shadow-2xl",
    justifyContent && justifyContentMapping[justifyContent],
    justifySelf && justifySelfMapping[justifySelf],
    justifyItems && justifyItemsMapping[justifyItems],
    alignItems && alignItemsMapping[alignItems],
    rounded === "full"
      ? "rounded-full"
      : rounded === "lg"
        ? "rounded-lg"
        : rounded
          ? "rounded-sm"
          : undefined,
    roundedTop === "full"
      ? "rounded-t-full"
      : roundedTop === "lg"
        ? "rounded-t-lg"
        : roundedTop
          ? "rounded-t"
          : undefined,
    roundedBottom === "full"
      ? "rounded-b-full"
      : roundedBottom === "lg"
        ? "rounded-b-lg"
        : roundedBottom
          ? "rounded-b"
          : undefined,
    whitespace && "whitespace-nowrap",
    textAlign === "left"
      ? "text-left"
      : textAlign === "center"
        ? "text-center"
        : textAlign === "right"
          ? "text-right"
          : undefined,
    textSize && textSizeMapping[textSize],
    fontWeight && fontWeightMapping[fontWeight],
    italic === true ? "italic" : italic === false ? "not-italic" : undefined,
    container && containerClassName(container),
    grow !== undefined && (grow === true ? "grow" : grow === false ? "grow-0" : "grow-golden"),
  );
}

export const boxClassNames = (props: BoxProps): string =>
  classNames(
    commonPropsToClassName(props),
    !props.display || props.display === "flex"
      ? "flex"
      : props.display === "inline-flex"
        ? "inline-flex"
        : "block",
    props.vertical && "flex-col",
    props.flex === "auto" && "flex-auto",
    props.flex === "none" && "flex-none",
    props.wrap && "flex-wrap",
    props.sticky && "sticky z-1",
  );
